import { FC } from 'react'
import { SectionExternalArtifactProps } from '@portfolio/models'
import { SectionWrapper } from '@portfolio/components'
import { styled } from '@portfolio/styles'

const DEFAULT_ASPECT_RATIO_WIDTH = 16
const DEFAULT_ASPECT_RATIO_HEIGHT = 9

const StyledSectionWrapper = styled(SectionWrapper, {
  maxWidth: '$richTextWidth',
})

const Figure = styled('figure', {
  margin: 0,
  display: 'grid',
  rowGap: '$20',
})

const ResponsiveFrameWrapper = styled('div', {
  position: 'relative',
  width: '100%',
  variants: {
    visible: {
      always: {
        display: 'block',
        '@md': {
          display: 'block',
        },
      },
      onlyMobile: {
        display: 'block',
        '@md': {
          display: 'none',
        },
      },
      onlyDesktop: {
        display: 'none',
        '@md': {
          display: 'block',
        },
      },
    },
  },
})

const Frame = styled('div', {
  position: 'relative',
  width: '100%',
})

const ArtifactIframe = styled('iframe', {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  border: 'none',
})

const Caption = styled('figcaption', {
  projectFont: 'body04',
  color: '$black_06',
})

const buildAspectRatio = (width?: number, height?: number): string => {
  const safeWidth =
    typeof width === 'number' && width > 0 ? width : DEFAULT_ASPECT_RATIO_WIDTH
  const safeHeight =
    typeof height === 'number' && height > 0
      ? height
      : DEFAULT_ASPECT_RATIO_HEIGHT

  return `${safeWidth} / ${safeHeight}`
}

type ArtifactFrameProps = {
  artifactUrl: string
  title: string
  aspectRatio: string
}

const ArtifactFrame: FC<ArtifactFrameProps> = ({
  artifactUrl,
  title,
  aspectRatio,
}) => {
  return (
    <Frame style={{ aspectRatio }}>
      <ArtifactIframe
        src={artifactUrl}
        sandbox="allow-scripts"
        loading="lazy"
        title={title}
      />
      <span className="sr-only">{title}</span>
    </Frame>
  )
}

export const SectionExternalArtifact: FC<SectionExternalArtifactProps> = ({
  artifactUrl,
  artifactUrlMobile,
  altText,
  caption,
  aspectRatioWidth,
  aspectRatioHeight,
  aspectRatioMobileWidth,
  aspectRatioMobileHeight,
}) => {
  if (!artifactUrl) {
    return null
  }

  const title = altText || 'External artifact'
  const desktopAspectRatio = buildAspectRatio(
    aspectRatioWidth,
    aspectRatioHeight,
  )
  const mobileAspectRatio = buildAspectRatio(
    aspectRatioMobileWidth ?? aspectRatioWidth,
    aspectRatioMobileHeight ?? aspectRatioHeight,
  )
  const desktopVisibility = artifactUrlMobile ? 'onlyDesktop' : 'always'

  return (
    <StyledSectionWrapper type="RICH_TEXT_SM">
      <Figure>
        <ResponsiveFrameWrapper visible={desktopVisibility}>
          <ArtifactFrame
            artifactUrl={artifactUrl}
            title={title}
            aspectRatio={desktopAspectRatio}
          />
        </ResponsiveFrameWrapper>
        {artifactUrlMobile && (
          <ResponsiveFrameWrapper visible="onlyMobile">
            <ArtifactFrame
              artifactUrl={artifactUrlMobile}
              title={title}
              aspectRatio={mobileAspectRatio}
            />
          </ResponsiveFrameWrapper>
        )}
        {caption && <Caption>{caption}</Caption>}
      </Figure>
    </StyledSectionWrapper>
  )
}
